/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { addHours, format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { formataTelefone, TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Loading from '../../../components/Loading';
import PageInfo from '../../../components/PageInfo';

import getValidationErrors from '../../../utils/getValidationErrors';

import HeaderRelatorio from '../HeaderRelatorio';
import getResponseErrors from '../../../utils/getResponseErrors';
import Paginacao from '../../../components/Paginacao';
import { IReturnInstanceInfo } from '../../Whatsapp';
import ItemTabela from './ItemTabela';

export interface IAtendimentoParticularDTO {
  AgeCodigo: number;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  ConNome: string;
  PacCodigo: number;
  PacNome: string;
  PacCelular: string;
  PacTelefone: string;
  PacCelularFormatado: string;
  PacEmail: string;
  DataUltimaNotificacao?: Date;
  DataUltimaNotificacaoFormatado: string;
  textoMensagem: string;
}

export interface IRelatorio {
  total: number;
  registros: IAtendimentoParticularDTO[];
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
}

interface IPaginacao extends IFormBusca {
  limite: number;
  pagina: number;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const AtendimentoParticular: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();

  const [instanceInfo, setInstanceInfo] = useState<IReturnInstanceInfo>({
    error: true,
    message: 'não conectado',
  });

  useEffect(() => {
    async function loadDadosInstancia(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IReturnInstanceInfo>('whatsapp/status');
        setInstanceInfo(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    loadDadosInstancia();
  }, []);

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.ATENDENTE) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<b>Descrição:</b> Exibe os pacientes que tiveram atendimento particular no período e não possuem um cartão ativo (em dia ou atrasado). `;

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<
    IAtendimentoParticularDTO[]
  >();
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        const { dataInicio, dataFim, limite, pagina } = paginacao;

        const dataInicioFormatada = format(dataInicio, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFim, 'yyyy-MM-dd');

        setDataExibe({
          dataDiaSemana: `${format(dataInicio, 'dd/MM/yyyy')} até ${format(
            dataFim,
            'dd/MM/yyyy',
          )}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        const response = await api.get<IRelatorio>(
          `/relatorios/atendimentoparticular?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}&limite=${limite}&pagina=${pagina}`,
        );

        setDadosRelatorio(undefined);

        const relatorio = response.data.registros.map(rel => {
          const DataUltimaNotificacaoFormatado = rel.DataUltimaNotificacao
            ? format(
                addHours(parseISO(rel.DataUltimaNotificacao.toString()), -3),
                'dd/MM/yyyy HH:mm',
              )
            : '';

          return {
            ...rel,
            AgeInicioFormatado: format(
              parseISO(rel.AgeInicio.toString()),
              'dd/MM/yyyy HH:mm',
            ),
            PacCelularFormatado: formataTelefone(rel.PacCelular),
            PacTelefone: formataTelefone(rel.PacTelefone),
            DataUltimaNotificacaoFormatado,
          };
        });

        setDadosRelatorio(relatorio);
        setTotalRegistros(
          response.data.total === 0 ? undefined : response.data.total,
        );
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Não foi possível carregar dados',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        setPaginacao({
          dataInicio: dataInicioSelecionada,
          dataFim: dataFimSelecionada,
          pagina: 1,
          limite: 50,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Atendimento Particular</h2>{' '}
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.ATENDENTE}
            />
          </div>
          {user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={3}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={3}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={3}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`ATENDIMENTO PARTICULAR - ${dataExibe?.dataDiaSemana || ''}`}
        />

        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <b>Total de {totalRegistros} registros</b>

              <Row noGutters className="bg-light py-2 border-top border-bottom">
                <Col md={2} lg={1}>
                  <b>Data Consulta</b>
                </Col>
                <Col md={3} lg={2}>
                  <b>Agenda</b>
                </Col>
                <Col md={3} lg={2}>
                  <b>Paciente</b>
                </Col>
                <Col md={1} lg={2}>
                  <b>Celular</b>
                </Col>
                <Col md={1} lg={2}>
                  <b>Telefone</b>
                </Col>
                <Col md={2} lg={1}>
                  <b>Último Envio</b>
                </Col>
                <Col md={2} lg={1}>
                  -
                </Col>
                <Col md={2} lg={1}>
                  -
                </Col>
              </Row>

              {dadosRelatorio &&
                dadosRelatorio.map(rel => (
                  <ItemTabela
                    atendimento={rel}
                    key={`Item-${rel.AgeCodigo.toString()}`}
                    conectado={
                      instanceInfo.instance_data?.phone_connected || false
                    }
                  />
                ))}
              <Paginacao
                limitePagina={paginacao?.limite || 50}
                paginasVizinhas={2}
                totalRegistros={totalRegistros}
                paginaInicio={paginacao?.pagina}
                handleProximaPagina={handleProximaPagina}
              />
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default AtendimentoParticular;
